import {IndiInfo} from '../topola'
import {analyticsEvent} from "../utils/google-analytics"
import {TopolaData} from '../utils/gedcom-utils'
import {GedcomLanguage as Language} from "../model/gedcom-language"
import {loadGedcom} from "./load-data"


export enum DataSourceEnum {
    UPLOADED,
    EDIT
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* Datasource types                                                                                                   */
/* ------------------------------------------------------------------------------------------------------------------ */
export interface UploadSourceSpec {
    source: DataSourceEnum.UPLOADED,
    hash: string,
    gedcom: string
    allLanguages: Language[]
    images?: Map<string, string>
}

export type EditSourceSpec = Omit<UploadSourceSpec, "source"> & { source: DataSourceEnum.EDIT }

export type DataSourceSpecs = UploadSourceSpec | EditSourceSpec

/* ------------------------------------------------------------------------------------------------------------------ */
/* Selection specs                                                                                                   */
/* ------------------------------------------------------------------------------------------------------------------ */

/** Source specification together with individual selection. */
export interface SourceSelection<SourceSpecT> {
    spec: SourceSpecT
    selection?: IndiInfo
    allLanguages?: Language[]
}

/* ------------------------------------------------------------------------------------------------------------------ */
/* Datasource specs                                                                                                   */
/* ------------------------------------------------------------------------------------------------------------------ */

/** Interface encapsulating functions specific for a data source. */
export interface DataSourceSpec<SourceSpecT> {

    /** Returns true if the application is now loading a completely new data set and the existing one should be wiped. */
    isNewData(newSource: SourceSelection<SourceSpecT>, oldSource: SourceSelection<SourceSpecT>, topolaData?: TopolaData): boolean

    /** Loads data from the data source. */
    loadData(spec: SourceSelection<SourceSpecT>): Promise<TopolaData>
}

/**
 * Files opened from the local computer.
 */
export class UploadedDataSource implements DataSourceSpec<UploadSourceSpec> {

    isNewData(newSource: SourceSelection<UploadSourceSpec>, oldSource: SourceSelection<UploadSourceSpec>, _topolaData?: TopolaData): boolean {
        return newSource.spec.hash !== oldSource.spec.hash
    }

    async loadData(source: SourceSelection<UploadSourceSpec>): Promise<TopolaData> {
        try {
            analyticsEvent("gedcom_upload")
            return await loadGedcom(source.spec.hash, source.spec.gedcom, source.spec.allLanguages, source.spec.images)
        } catch (e) {
            throw e
        }
    }
}

/**
 * GEDCOM file that after loaded wants to be edited on the Creator.
 */
export class GedcomEditDataSource implements DataSourceSpec<EditSourceSpec> {

    isNewData(newSource: SourceSelection<EditSourceSpec>, oldSource: SourceSelection<EditSourceSpec>, _topolaData?: TopolaData): boolean {
        return newSource.spec.hash !== oldSource.spec.hash
    }

    async loadData(source: SourceSelection<EditSourceSpec>): Promise<TopolaData> {
        try {
            analyticsEvent("gedcom_edit")
            return await loadGedcom(source.spec.hash, source.spec.gedcom, source.spec.allLanguages, source.spec.images)
        } catch (e) {
            throw e
        }
    }
}